import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import ChecklistSection from "./ChecklistSection";
import RecurrenceSection from "./RecurrenceSection";
import TaskDependencies from "./TaskDependencies";

const statuses = [
  { value: "todo", label: "Å Gjøre" },
  { value: "in_progress", label: "Pågår" },
  { value: "completed", label: "Fullført" },
  { value: "cancelled", label: "Kansellert" },
];

const priorities = [
  { value: "low", label: "Lav" },
  { value: "medium", label: "Middels" },
  { value: "high", label: "Høy" },
  { value: "urgent", label: "Haster" },
];

const getDisplayName = (user) => {
  if (user?.first_name || user?.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user?.full_name || user?.email || "";
};

export default function TaskForm({ task, relatedTo, relatedId, relatedName, parentTaskId, projectId, projectName, phaseId, phases, projects, onSubmit, onClose, isLoading }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "todo",
    priority: "medium",
    assigned_to: "",
    due_date: "",
    estimated_hours: "",
    related_to_type: relatedTo || "none",
    related_to_id: relatedId || "",
    parent_task_id: parentTaskId || "",
    project_id: projectId || "",
    phase_id: phaseId || "",
    checklist: [],
    is_recurring: false,
    recurrence_pattern: "weekly",
    recurrence_interval: 1,
    recurrence_end_date: "",
    depends_on: [],
    });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const { data: investors = [] } = useQuery({
    queryKey: ["investors"],
    queryFn: () => base44.entities.Investor.list(),
    enabled: !relatedTo,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: () => base44.entities.BusinessCustomer.list(),
    enabled: !relatedTo,
  });

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || "",
        description: task.description || "",
        status: task.status || "todo",
        priority: task.priority || "medium",
        assigned_to: task.assigned_to || "",
        due_date: task.due_date || "",
        estimated_hours: task.estimated_hours || "",
        related_to_type: task.related_to_type || relatedTo || "none",
        related_to_id: task.related_to_id || relatedId || "",
        parent_task_id: task.parent_task_id || parentTaskId || "",
        project_id: task.project_id || projectId || "",
        phase_id: task.phase_id || phaseId || "",
        checklist: task.checklist || [],
        is_recurring: task.is_recurring || false,
        recurrence_pattern: task.recurrence_pattern || "weekly",
        recurrence_interval: task.recurrence_interval || 1,
        recurrence_end_date: task.recurrence_end_date || "",
        depends_on: task.depends_on || [],
        });
        }
        }, [task, relatedTo, relatedId, parentTaskId, projectId, phaseId]);

        const { data: allTasks = [] } = useQuery({
        queryKey: ["tasks"],
        queryFn: () => base44.entities.Task.list(),
        enabled: !!projectId,
        });

  const handleSubmit = (e) => {
    e.preventDefault();
    const assignedUser = users.find(u => u.email === formData.assigned_to);
    
    let relatedNameValue = relatedName;
    if (!relatedTo && formData.related_to_type !== "none" && formData.related_to_id) {
      if (formData.related_to_type === "investor") {
        const investor = investors.find(i => i.id === formData.related_to_id);
        relatedNameValue = investor?.name;
      } else if (formData.related_to_type === "customer") {
        const customer = customers.find(c => c.id === formData.related_to_id);
        relatedNameValue = customer?.company_name;
      }
    }
    
    let phaseName = null;
    if (formData.phase_id && phases) {
      const phase = phases.find(p => p.id === formData.phase_id);
      phaseName = phase?.name;
    }

    let projectNameValue = projectName;
    if (!projectId && formData.project_id && projects) {
      const project = projects.find(p => p.id === formData.project_id);
      projectNameValue = project?.name;
    }

    const data = {
      ...formData,
      assigned_to_name: assignedUser ? getDisplayName(assignedUser) : formData.assigned_to,
      related_to_type: relatedTo || formData.related_to_type,
      related_to_id: relatedId || (formData.related_to_type !== "none" ? formData.related_to_id : null),
      related_to_name: relatedNameValue || null,
      project_id: projectId || formData.project_id || null,
      project_name: projectNameValue || null,
      phase_id: formData.phase_id || null,
      phase_name: phaseName || null,
    };
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-semibold text-slate-900">
            {task ? "Rediger Oppgave" : (formData.parent_task_id ? "Opprett Underoppgave" : "Opprett Oppgave")}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="title">Oppgavetittel *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="h-11"
              placeholder="Hva skal gjøres?"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beskrivelse</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Legg til detaljer om oppgaven..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="assigned_to">Tildel Til *</Label>
              <Select value={formData.assigned_to} onValueChange={(v) => setFormData({ ...formData, assigned_to: v })}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Velg bruker" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.email}>
                      {getDisplayName(user)} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="due_date">Forfallsdato</Label>
              <Input
                id="due_date"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimated_hours">Estimert tidsbruk (timer)</Label>
              <Input
                id="estimated_hours"
                type="number"
                min="0"
                step="0.5"
                value={formData.estimated_hours}
                onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value ? parseFloat(e.target.value) : "" })}
                className="h-11"
                placeholder="f.eks. 8"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Prioritet</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorities.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!projectId && projects && projects.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="project_id">Prosjekt (Valgfritt)</Label>
              <Select 
                value={formData.project_id} 
                onValueChange={(v) => setFormData({ ...formData, project_id: v })}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Velg prosjekt" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>Ingen prosjekt</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {!relatedTo ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="related_to_type">Relatert Til</Label>
                <Select 
                  value={formData.related_to_type} 
                  onValueChange={(v) => setFormData({ ...formData, related_to_type: v, related_to_id: "" })}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ingen</SelectItem>
                    <SelectItem value="investor">Investor</SelectItem>
                    <SelectItem value="customer">Kunde</SelectItem>
                    <SelectItem value="internal">Internt</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.related_to_type === "investor" && (
                <div className="space-y-2">
                  <Label htmlFor="related_to_id">Velg Investor</Label>
                  <Select 
                    value={formData.related_to_id} 
                    onValueChange={(v) => setFormData({ ...formData, related_to_id: v })}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Velg investor" />
                    </SelectTrigger>
                    <SelectContent>
                      {investors.map((investor) => (
                        <SelectItem key={investor.id} value={investor.id}>
                          {investor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.related_to_type === "customer" && (
                <div className="space-y-2">
                  <Label htmlFor="related_to_id">Velg Kunde</Label>
                  <Select 
                    value={formData.related_to_id} 
                    onValueChange={(v) => setFormData({ ...formData, related_to_id: v })}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Velg kunde" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-600">
                Relatert til: <span className="font-medium text-slate-900">{relatedName}</span>
              </p>
            </div>
          )}

          <ChecklistSection
            checklist={formData.checklist}
            onChange={(checklist) => setFormData({ ...formData, checklist })}
          />

          <RecurrenceSection
            formData={formData}
            onChange={setFormData}
          />

          {projectId && !parentTaskId && (
            <TaskDependencies
              dependencies={formData.depends_on}
              availableTasks={allTasks.filter(t => t.project_id === projectId && !t.parent_task_id)}
              onChange={(depends_on) => setFormData({ ...formData, depends_on })}
              currentTaskId={task?.id}
            />
          )}

          {phases && phases.length > 0 && !parentTaskId && (
            <div className="space-y-2">
              <Label htmlFor="phase_id">Fase (Valgfritt)</Label>
              <Select 
                value={formData.phase_id} 
                onValueChange={(v) => setFormData({ ...formData, phase_id: v })}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Velg fase" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>Ingen fase</SelectItem>
                  {phases.map((phase) => (
                    <SelectItem key={phase.id} value={phase.id}>
                      {phase.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Avbryt</Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800">
              {isLoading ? "Lagrer..." : task ? "Oppdater Oppgave" : "Opprett Oppgave"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}